import React from "react";
import { motion } from "framer-motion";

const reasons = [
  {
    num: "01",
    title: "Singapore-based",
    text:
      "Operating from a well-regulated trading jurisdiction with strong financial and legal infrastructure.",
  },
  {
    num: "02",
    title: "Developing supplier network",
    text:
      "Direct relationships with mills, refiners, and processors across selected producing regions of Asia.",
  },
  {
    num: "03",
    title: "Reliable execution",
    text:
      "We deliver on commitments — contractually, operationally, and commercially.",
  },
  {
    num: "04",
    title: "Long-term orientation",
    text:
      "Counterparties partner with us across multiple cargos and cycles.",
  },
  {
    num: "05",
    title: "Integrity & discipline",
    text:
      "Trade operations governed by transparency, compliance, and commercial discretion.",
  },
];

const values = [
  "Integrity",
  "Reliability",
  "Growth",
  "Transparency",
  "Relationship-Led",
];

export const WhyVynex = () => {
  return (
    <section
      id="why"
      className="relative bg-vynex-navy text-white py-28 lg:py-36 overflow-hidden"
      data-testid="why-vynex-section"
    >
      <div className="absolute inset-0 grain" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-vynex-gold/50 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-vynex-gold" />
              <span className="eyebrow text-vynex-gold">Our Approach</span>
            </div>
            <h2 className="font-display text-4xl lg:text-[56px] leading-[1.05] tracking-tight">
              Built on{" "}
              <span className="italic text-vynex-silver">
                disciplined principles.
              </span>
            </h2>
            <p className="mt-8 text-vynex-silver/70 leading-relaxed max-w-md">
              Five principles that shape how we approach every counterparty,
              every contract, and every cargo.
            </p>
          </div>

          <div className="lg:col-span-7">
            <ul className="divide-y divide-white/10 border-y border-white/10">
              {reasons.map((r, i) => (
                <motion.li
                  key={r.title}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="py-7 grid grid-cols-12 gap-6 group cursor-default"
                  data-testid={`why-reason-${i}`}
                >
                  <span className="col-span-2 font-display text-vynex-gold/70 text-lg">
                    {r.num}
                  </span>
                  <div className="col-span-10">
                    <h3 className="font-display text-2xl lg:text-3xl text-white group-hover:text-vynex-gold transition-colors">
                      {r.title}
                    </h3>
                    <p className="mt-2 text-vynex-silver/70 leading-relaxed max-w-2xl">
                      {r.text}
                    </p>
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>

        {/* Brand values - dot prefix BEFORE each value */}
        <div className="mt-24 border-t border-white/10 pt-12">
          <div className="eyebrow text-vynex-gold mb-8">Brand Values</div>
          <div className="flex flex-wrap items-center gap-x-10 gap-y-6">
            {values.map((v, i) => (
              <motion.div
                key={v}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="flex items-center gap-4 group"
                data-testid={`brand-value-${i}`}
              >
                <span className="w-2.5 h-2.5 bg-vynex-gold rounded-full shrink-0" />
                <span className="font-display text-3xl lg:text-5xl text-white group-hover:text-vynex-gold transition-colors cursor-default">
                  {v}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyVynex;
